/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { toast } from 'react-toastify';

// Components
import SecondaryButton from '../SecondaryButton';
import Table from '../Table';
import Modal from '../Modal_2';

// Icons
import DeleteIcon from '../../../assets/images/svg/delete-icon.svg';
import AttentionIcon from '../../../assets/images/svg/attention.svg';

// Tools
import { hourFormat } from '../../tools/manager.js';

// @env
const S3url = process.env.REACT_APP_URL_S3_OBJECT;

const ListVoiceNotes = () => {
  // 🌱 Store
  const currentUser = useStoreState((state) => state.userData.currentUser);
  const getListVoiceNote = useStoreActions((actions) => actions.middleware.VoiceNoteMiddleware.getListVoiceNotes);
  const deleteVoiceNote = useStoreActions((actions) => actions.middleware.VoiceNoteMiddleware.deleteVoiceNote);
  const deleteFile = useStoreActions((actions) => actions.middleware.FileS3.deleteFile);
  const getSignedUrlS3 = useStoreActions((actions) => actions.middleware.FileS3.getSignedUrlS3);

  // 🌱 États
  const [voiceNotes, setVoiceNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [voiceNoteSelected, setVoiceNoteSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // 🌱 Charger les notes vocales
  const callListVoiceNote = async (id) => {
    setLoading(true);
    try {
      const res = await getListVoiceNote(id);
      if (!res || res.length === 0) {
        setVoiceNotes([]);
        return;
      }

      // Tri décroissant
      const sortedNotes = res.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      // URLs S3 signées
      const urlsResults = await Promise.allSettled(
        sortedNotes.map((note) => getSignedUrlS3(note.audioUri))
      );

      const notesWithUrls = sortedNotes.map((note, index) => {
        const result = urlsResults[index];
        return {
          ...note,
          audioUrl: result.status === 'fulfilled' ? result.value : `${S3url}${note.audioUri}`,
          signedUrlError: result.status === 'rejected' ? result.reason : null,
        };
      });

      setVoiceNotes(notesWithUrls);
    } catch (error) {
      console.error('Erreur chargement notes vocales:', error);
      toast.error('❗️ Une erreur est survenue lors du chargement des notes vocales');
    } finally {
      setLoading(false);
    }
  };

  // 🌱 Supprimer une note vocale
  const handleDeleteVoiceNote = async () => {
    if (!voiceNoteSelected) return;

    try {
      await deleteFile({ nameFile: voiceNoteSelected.audioUri });
      await deleteVoiceNote(voiceNoteSelected.id);
      toast.success('✅ Note vocale supprimée avec succès');
      setVoiceNoteSelected(null);
      setShowModal(false);
      callListVoiceNote(currentUser?.id);
    } catch (error) {
      console.error('Erreur suppression note vocale:', error);
      toast.error('❗️ Impossible de supprimer la note vocale');
    }
  };

  // 🌱 useEffect initial
  useEffect(() => {
    if (currentUser?.id) callListVoiceNote(currentUser.id);
  }, [currentUser]);

  return (
    <section className="px-4 md:pt-14 md:px-9">
      {/* Header */}
      <div className="flex flex-col justify-between pb-4 mb-4 space-y-4 border-b border-gray-admin md:flex-row md:space-y-0">
        <h3 className="text-xl font-bold">Liste des notes vocales</h3>
        <SecondaryButton title="+ Ajouter une nouvelle note vocale" link="add" />
      </div>

      {/* Table */}
      <div className="mt-9">
        {loading ? (
          <p className="text-center py-8 text-gray-500">Chargement des notes vocales...</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Prénom</th>
                <th>Date</th>
                <th>Heure</th>
                <th>Audio</th>
                <th className="flex justify-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {voiceNotes.length > 0 ? (
                voiceNotes.map((note) => {
                  const date = new Date(note.createdAt).toLocaleDateString();
                  const time = hourFormat(note.createdAt).full;
                  return (
                    <tr key={note.id}>
                      <td>{note.firstname}</td>
                      <td>{note.lastname}</td>
                      <td>{date}</td>
                      <td>{time}</td>
                      <td className="py-4">
                        {note.audioUrl ? (
                          <audio controls className="w-full">
                            <source src={note.audioUrl} type="audio/mpeg" />
                          </audio>
                        ) : (
                          <span className="text-red-500 text-sm">Erreur lecture audio</span>
                        )}
                      </td>
                      <td className="w-fit text-right">
                        <button
                          className="px-2 py-2 border rounded-lg border-gray-separate"
                          onClick={() => {
                            setVoiceNoteSelected(note);
                            setShowModal(true);
                          }}
                        >
                          <img src={DeleteIcon} alt="Supprimer" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="text-center text-gray-500 py-4">
                    Vous n'avez reçu aucune note vocale
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      {/* Modal de suppression */}
      <Modal isVisible={showModal}>
        <div className="pt-5 pl-5">
          <img src={AttentionIcon} alt="Attention" />
          <p className="px-5 pt-2 text-gray-admin">
            Êtes-vous sûr(e) de vouloir supprimer cette note vocale ? Cette action est définitive.
          </p>
          <div className="text-center pt-7">
            <button
              className="px-12 py-3 mr-4 font-semibold border rounded-xl text-red-error"
              onClick={() => setShowModal(false)}
            >
              Annuler
            </button>
            <button
              className="px-10 py-3 text-white bg-red-error rounded-xl"
              onClick={handleDeleteVoiceNote}
            >
              Supprimer
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default ListVoiceNotes;
